import React, {
  useEffect,
  forwardRef,
  useImperativeHandle,
  memo,
  useState,
  useCallback,
  useMemo,
} from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import Select from "react-select";
import {
  personalInfoFields,
  presentAddressFields,
  permanentAddressFields,
} from "./ProfileData";
import {
  getPersonalInfo,
  updatePersonalInfo,
} from "../../../ApiHelper/url_Helpers.js";
import { Country, State } from "country-state-city";
import Loader from "../../../Components/Loader.jsx";
import { RiseLoader } from "react-spinners";

// --- Validation Schema ---
const validationSchema = Yup.object().shape({
  personal_info: Yup.object().shape(
    personalInfoFields.reduce((acc, field) => {
      if (field.id === "date_of_birth") {
        acc[field.id] = Yup.date()
          .required(`${field.label} is required`)
          .max(
            new Date(new Date().setFullYear(new Date().getFullYear() - 18)),
            "Must be at least 18 years old",
          );
      } else if (field.id === "personal_email") {
        acc[field.id] = Yup.string()
          .required(`${field.label} is required`)
          .matches(/^[^\s@]+@[^\s@]+\.[A-Za-z]{2,}$/, "Enter a valid email");
      } else if (field.required) {
        acc[field.id] = Yup.string().required(`${field.label} is required`);
      }
      return acc;
    }, {}),
  ),
  permanent: Yup.object().shape(
    permanentAddressFields.reduce((acc, field) => {
      if (field.id === "phone_number") {
        acc[field.id] = Yup.string()
          .required()
          .test("is-valid-phone", "Invalid phone number", (value) => {
            if (!value) return true;
            return /^[0-9]{10}$/.test(value);
          });
      } else if (field.required) {
        acc[field.id] = Yup.string().required(`${field.label} is required`);
      }
      return acc;
    }, {}),
  ),
  present: Yup.object().shape(
    presentAddressFields.reduce((acc, field) => {
      if (field.id === "phone_number") {
        acc[field.id] = Yup.string()
          .required()
          .test("is-valid-phone", "Invalid phone number", (value) => {
            if (!value) return true;
            return /^[0-9]{10}$/.test(value);
          });
      } else if (field.required) {
        acc[field.id] = Yup.string().required(`${field.label} is required`);
      }
      return acc;
    }, {}),
  ),
});

// --- Form Field Component ---
const FormField = memo(
  ({ field, name, value, onChange, onBlur, error, touched, readOnly }) => {
    const isError = touched && Boolean(error);

    const selectOptions = useMemo(
      () => field.options?.map((opt) => ({ label: opt, value: opt })) || [],
      [field.options],
    );
    const currentValue = selectOptions.find((o) => o.value === value) || null;

    const customStyles = {
      control: (base, state) => ({
        ...base,

        minHeight: "38px",

        borderRadius: "0.5rem",

        fontSize: "14px",

        backgroundColor: readOnly ? "#F3F4F6" : "#FAFBFD", // Locked fields ke liye gray background

        borderColor: isError
          ? "#ef4444"
          : state.isFocused
            ? "#00B8D9"
            : "#d1d5db",

        boxShadow: state.isFocused ? "0 0 0 1px #00B8D9" : "none",

        "&:hover": { borderColor: state.isFocused ? "#00B8D9" : "#d1d5db" },

        cursor: readOnly ? "not-allowed" : "default",
      }),

      menuPortal: (base) => ({ ...base, zIndex: 9999 }),

      menu: (base) => ({ ...base, zIndex: 9999 }),
    };

    return (
      <div className="flex flex-col justify-start p-2 md:p-4 gap-1.5 w-full min-w-0 box-border overflow-hidden">
        <label
          htmlFor={name}
          className="text-[13px] md:text-sm font-semibold text-gray-700 truncate"
        >
          {field.label}

          {field.required && !readOnly && (
            <span className="text-red-500 ml-1 font-bold">*</span>
          )}
        </label>

        {field.type === "select" ? (
          <Select
            id={name}
            instanceId={name}
            options={selectOptions}
            value={currentValue}
            isDisabled={readOnly} // Yahan se dropdown freeze hoga
            placeholder="Select..."
            styles={customStyles}
            isSearchable={true}
            menuPortalTarget={document.body}
            onChange={(selected) => {
              onChange({
                target: { name, value: selected ? selected.value : "" },
              });
            }}
            onBlur={() => onBlur({ target: { name } })}
          />
        ) : (
          <input
            id={name}
            name={name}
            type={field.type}
            value={value || ""}
            onChange={onChange}
            onBlur={onBlur}
            readOnly={readOnly} // Yahan se input freeze hoga
            placeholder={field.placeholder}
            max={
              field.id === "date_of_birth"
                ? new Date(
                    new Date().setFullYear(new Date().getFullYear() - 18),
                  )
                    .toISOString()
                    .split("T")[0]
                : undefined
            }
            className={`border rounded-lg px-3 py-2 text-sm w-full outline-none transition-all box-border ${
              readOnly ? "bg-gray-100 cursor-not-allowed" : ""
            } ${
              isError
                ? "border-red-500 bg-red-50 ring-1 ring-red-200"
                : "border-gray-300 bg-[#FAFBFD] focus:ring-1 focus:ring-[#00B8D9]"
            }`}
          />
        )}

        <div className="min-h-[16px]">
          {isError && !readOnly && (
            <p className="text-red-500 text-[10px] md:text-xs italic font-medium">
              {error}
            </p>
          )}
        </div>
      </div>
    );
  },
);

// --- Main Component ---

const PersonalInfo = forwardRef(({ onEditStatusChange, onDirtyChange }, ref) => {
  const [loading, setLoading] = useState(true);

  const [isSameAsPermanent, setIsSameAsPermanent] = useState(false);

  const [dbData, setDbData] = useState({
    personal_info: {},
    permanent: {},
    present: {},
  });

  const [isEditable, setIsEditable] = useState(false); // Master control state

  const [permStates, setPermStates] = useState([]);

  const [presStates, setPresStates] = useState([]);

  const countries = useMemo(
    () => Country.getAllCountries().map((c) => c.name),
    [],
  );

  const getStateOptions = useCallback((countryName) => {
    const country = Country.getAllCountries().find(
      (c) => c.name === countryName,
    );

    return country
      ? State.getStatesOfCountry(country.isoCode).map((s) => s.name)
      : [];
  }, []);

  const formik = useFormik({
    initialValues: dbData,

    enableReinitialize: true,

    validationSchema,
  });

  useEffect(() => {
    if (onDirtyChange) {
      onDirtyChange(formik.dirty);
    }
  }, [formik.dirty, onDirtyChange]);

  const fetchProfileData = useCallback(async () => {
    try {
      setLoading(true);

      const response = await getPersonalInfo();

      if (response) {
        // Backend action check

        const canEdit = response.personal_info?.action?.isEdit ?? false;

        setIsEditable(canEdit);
        onEditStatusChange(canEdit);
        const info = response.personal_info?.data || {};

        const cap = (s) =>
          s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : "";

        if (info.gender) info.gender = cap(info.gender);

        if (info.marital_status) info.marital_status = cap(info.marital_status);

        info.is_physically_challenged =
          info.is_physically_challenged === true ? "Yes" : "No";

        info.is_international_employee =
          info.is_international_employee === true ? "Yes" : "No";

        // Address mapping based on response structure

        const addrList = response.addresses?.data || [];

        const perm = addrList.find((a) => a.address_type === "permanent") || {};

        const pres = addrList.find((a) => a.address_type === "present") || {};

        const addressFieldIds = permanentAddressFields.map((field) => field.id);
        const normalizeAddressValue = (value) => String(value ?? "").trim();
        const addressesMatch =
          addressFieldIds.some(
            (fieldId) => normalizeAddressValue(perm[fieldId]) !== "",
          ) &&
          addressFieldIds.every(
            (fieldId) =>
              normalizeAddressValue(perm[fieldId]) ===
              normalizeAddressValue(pres[fieldId]),
          );

        setIsSameAsPermanent(addressesMatch);

        if (perm.country) setPermStates(getStateOptions(perm.country));

        if (pres.country) setPresStates(getStateOptions(pres.country));

        setDbData({ personal_info: info, permanent: perm, present: pres });
      }
    } catch (err) {
      console.error("Fetch Error:", err);
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  }, [getStateOptions]);

  useEffect(() => {
    fetchProfileData();
  }, [fetchProfileData]);

  const injectOptions = (fields, type) => {
    return fields.map((f) => {
      if (f.id === "country")
        return { ...f, options: countries, type: "select" };

      if (f.id === "state")
        return {
          ...f,
          options: type === "perm" ? permStates : presStates,
          type: "select",
        };

      return f;
    });
  };

  useImperativeHandle(ref, () => ({
    handleChildSave: async () => {
      if (!isEditable)
        return { success: false, error: "Editing is not allowed" };

      formik.setTouched({
        personal_info: personalInfoFields.reduce(
          (a, f) => ({ ...a, [f.id]: true }),
          {},
        ),

        permanent: permanentAddressFields.reduce(
          (a, f) => ({ ...a, [f.id]: true }),
          {},
        ),

        present: presentAddressFields.reduce(
          (a, f) => ({ ...a, [f.id]: true }),
          {},
        ),
      });

      const errors = await formik.validateForm();

      if (Object.keys(errors).length > 0)
        return { success: false, error: "Validation Failed" };

      try {
        const payload = {
          personal_info: {
            ...formik.values.personal_info,

            gender: formik.values.personal_info.gender?.toLowerCase(),

            marital_status:
              formik.values.personal_info.marital_status?.toLowerCase(),

            is_physically_challenged:
              formik.values.personal_info.is_physically_challenged === "Yes",

            is_international_employee:
              formik.values.personal_info.is_international_employee === "Yes",
          },

          addresses: [
            { ...formik.values.permanent, address_type: "permanent" },

            { ...formik.values.present, address_type: "present" },
          ],
        };

        await updatePersonalInfo(payload);

        // Manual Freeze: Save hote hi state false kar do

        setIsEditable(false);

        await fetchProfileData();

        return { success: true };
      } catch (err) {
        return { success: false, error: err.response?.data || "API Error" };
      }
    },
  }));

  const toggleSameAsPermanent = (e) => {
    if (!isEditable) return; // Prevent toggle if locked

    const checked = e.target.checked;

    setIsSameAsPermanent(checked);

    if (checked) {
      formik.setFieldValue("present", { ...formik.values.permanent });

      setPresStates(permStates);
    }
  };

  return (
    <div className="flex flex-col gap-6 w-full max-w-full overflow-x-hidden pb-10  box-border">
      {/* 1. Personal Information Section */}
      {loading ? (
        <div
          className={`flex items-center justify-center w-full h-full min-h-[350px] bg-white`}
        >
          <RiseLoader color="#35e6e9" />
        </div>
      ) : (
        <div>
          <div className="border-black/5 border-2 bg-white rounded-lg shadow-sm overflow-hidden w-full">
            <div className="flex justify-between items-center px-4 py-3 border-b bg-gray-50/50">
              <h3 className="font-semibold text-black text-sm md:text-base">
                1. Personal Information
              </h3>

              {!isEditable && (
                <span className="text-[10px] bg-amber-100 text-amber-700 px-2 py-1 rounded font-bold uppercase">
                  Locked
                </span>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 w-full box-border">
              {personalInfoFields
                .filter((field) => {
                  const pInfo = formik.values.personal_info || {};
                  if (
                    (field.id === "marriage_date" ||
                      field.id === "spouse_name") &&
                    pInfo.marital_status === "Single"
                  ) {
                    return false;
                  }
                  if (
                    field.id === "disability_type" &&
                    pInfo.is_physically_challenged !== "Yes"
                  ) {
                    return false;
                  }
                  return true;
                })
                .map((field) => {
                  let currentField = field;
                  if (
                    field.id === "country_of_origin" ||
                    field.id === "nationality"
                  ) {
                    currentField = {
                      ...field,
                      options: countries,
                      type: "select",
                    };
                  }
                  return (
                    <div key={currentField.id} className="w-full min-w-0">
                      <FormField
                        field={currentField}
                        name={`personal_info.${currentField.id}`}
                        value={formik.values.personal_info?.[currentField.id]}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.errors.personal_info?.[currentField.id]}
                        touched={
                          formik.touched.personal_info?.[currentField.id]
                        }
                        readOnly={!isEditable}
                      />
                    </div>
                  );
                })}
            </div>
          </div>

          {/* 2. Address Section */}

          <div className="border-black/5 border-2 bg-white rounded-lg  mt-8 shadow-sm overflow-hidden w-full">
            <h3 className="font-semibold px-4 py-3 border-b text-black bg-gray-50/50 text-sm md:text-base">
              2. Address
            </h3>

            <h4 className="px-5 py-2 text-[10px] md:text-[11px] font-bold text-gray-400 uppercase tracking-widest mt-2">
              Permanent Address
            </h4>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 w-full border-b border-dashed border-gray-100 pb-2 box-border">
              {injectOptions(permanentAddressFields, "perm").map((field) => (
                <div key={`perm-${field.id}`} className="w-full min-w-0">
                  <FormField
                    field={field}
                    name={`permanent.${field.id}`}
                    value={formik.values.permanent?.[field.id]}
                    onChange={(e) => {
                      if (!isEditable) return;

                      formik.handleChange(e);

                      if (field.id === "country") {
                        const states = getStateOptions(e.target.value);

                        setPermStates(states);

                        if (isSameAsPermanent) {
                          setPresStates(states);

                          formik.setFieldValue(
                            `present.${field.id}`,
                            e.target.value,
                          );
                        }
                      } else if (isSameAsPermanent) {
                        formik.setFieldValue(
                          `present.${field.id}`,
                          e.target.value,
                        );
                      }
                    }}
                    onBlur={formik.handleBlur}
                    error={formik.errors.permanent?.[field.id]}
                    touched={formik.touched.permanent?.[field.id]}
                    readOnly={!isEditable}
                  />
                </div>
              ))}
            </div>

            <div className="flex items-center px-5 py-4 bg-blue-50/40 w-full box-border">
              <input
                type="checkbox"
                id="sameAsPerm"
                checked={isSameAsPermanent}
                onChange={toggleSameAsPermanent}
                disabled={!isEditable}
                className={`mr-3 h-4 w-4 md:h-5 md:w-5 cursor-pointer flex-shrink-0 ${!isEditable && "cursor-not-allowed"}`}
              />

              <label
                htmlFor="sameAsPerm"
                className={`font-bold text-xs md:text-sm text-black cursor-pointer select-none ${!isEditable && "text-gray-400"}`}
              >
                Same as Permanent Address
              </label>
            </div>

            <h4 className="px-5 py-2 text-[10px] md:text-[11px] font-bold text-gray-400 uppercase tracking-widest mt-2">
              Present Address
            </h4>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 w-full box-border">
              {injectOptions(presentAddressFields, "pres").map((field) => (
                <div key={`pres-${field.id}`} className="w-full min-w-0">
                  <FormField
                    field={field}
                    name={`present.${field.id}`}
                    value={formik.values.present?.[field.id]}
                    onChange={(e) => {
                      if (!isEditable) return;

                      formik.handleChange(e);

                      if (field.id === "country")
                        setPresStates(getStateOptions(e.target.value));
                    }}
                    onBlur={formik.handleBlur}
                    error={formik.errors.present?.[field.id]}
                    touched={formik.touched.present?.[field.id]}
                    readOnly={!isEditable || isSameAsPermanent}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default PersonalInfo;
